import React, { Fragment, useEffect, useRef, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { get } from 'lodash';
import type { FieldProps } from 'formik';
import 'formik';
import moment from 'moment';
import { getInputClasses, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { useDelayUnmount } from 'Src/hooks/use-unmount-delay/useUnmountDelay';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { Datepicker } from 'Components/frameworks/datepicker/Datepicker';
import { Label } from './Label';
type Props = FieldProps & {
  className?: string;
  customClasses?: string;
  dataTestId?: string;
  dateFormat?: string;
  defaultDuration?: number;
  disabledDays?: {
    after: Date;
    before: Date;
  };
  hasArrowDown?: boolean;
  isDateRange: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  language?: string;
  maxDate?: Date;
  minDate?: Date;
  placeholder?: string;
};

const clickedElementIs = (element: EventTarget | null, elementType: string, dataTestId: string) =>
  element instanceof Element &&
  !!element.closest(elementType) &&
  !!element.closest(elementType)?.matches(`[data-testid="${dataTestId}"]`);

export const DatePickerInput = ({
  className,
  customClasses,
  dataTestId,
  dateFormat = 'DD/MM/YYYY',
  defaultDuration = 1,
  disabledDays,
  field,
  form,
  hasArrowDown = false,
  isDateRange = false,
  isDisabled = false,
  isRequired = false,
  label,
  language,
  maxDate,
  minDate,
  placeholder,
}: Props) => {
  const [isActive, setIsActive] = useState(false);
  const datePickerReference = useRef(null);
  const { from, to } = get(form.values, field.name, {
    from: null,
    to: null,
  });
  const fromInputValue = from ? moment(from).format(dateFormat) : null;
  const toInputValue = to ? moment(to).format(dateFormat) : null;
  const inputValue: string = [fromInputValue, toInputValue].filter((value) => !!value).join(' — ');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(field, form, isDisabled);
  useEffect(() => {
    const onClickOutside = (e: Event) => {
      const { current: datePickerElement } = datePickerReference;
      const clickedElement = e.target;
      const clickedElementIsPrevArrow = clickedElementIs(
        clickedElement,
        'button',
        'datepicker-arrow-prev',
      );
      const clickedElementIsNextArrow = clickedElementIs(
        clickedElement,
        'button',
        'datepicker-arrow-next',
      );

      if (
        datePickerElement &&
        !datePickerElement.contains(clickedElement) &&
        !clickedElementIsPrevArrow &&
        !clickedElementIsNextArrow
      ) {
        // 1. Set "to" date based on default duration if it's not defined
        if (isDateRange && !!from && !to) {
          form.setFieldValue(field.name, {
            from,
            to: moment(from)
              .add({
                days: defaultDuration,
              })
              .toDate(),
          });
        }

        // 2. Close DatePicker
        setIsActive(false);
      }
    };

    document.addEventListener('click', onClickOutside);
    document.addEventListener('focus', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
      document.removeEventListener('focus', onClickOutside);
    };
  }, [from, to]);

  const onDaySelect = (
    selection:
      | Date
      | { from: Date; to: Date; modifiers: import('react-day-picker').DayModifiers }
      | { fromDate?: Date; toDate?: Date },
  ) => {
    const fromDate =
      selection instanceof Date
        ? selection
        : ((selection as { from?: Date; fromDate?: Date }).from ??
          (selection as { fromDate?: Date }).fromDate);
    const toDate =
      selection instanceof Date
        ? undefined
        : ((selection as { to?: Date; toDate?: Date }).to ??
          (selection as { toDate?: Date }).toDate);
    if (isDateRange && !!fromDate && !toDate) {
      form.setFieldValue(field.name, {
        from: fromDate,
        to: moment(fromDate)
          .add({
            days: defaultDuration,
          })
          .toDate(),
      });
    }

    // 2. Close DatePicker
    setIsActive(false);
  };

  const shouldRenderDatePicker = useDelayUnmount(isActive, 300);
  const classes = getInputClasses({
    hasHeight: true,
    hasValidationStatus: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  const datePickerStyle = customClasses ? customClasses : classes;
  return (
    <Fragment>
      {!!label && (
        <Label
          className="mx-15 mb-5"
          htmlFor={field.name}
          isErrored={isErrored}
          isRequired={isRequired}
          label={label}
        />
      )}
      <div className={classnames('relative isolate w-full', className)} ref={datePickerReference}>
        <div className="relative isolate flex w-full items-center">
          <input
            autoComplete="off"
            className={classnames('h-48', datePickerStyle)}
            data-testid={dataTestId}
            disabled={isDisabled}
            {...field}
            onFocus={() => setIsActive(true)}
            placeholder={placeholder}
            type="text"
            value={inputValue}
          />
          {hasArrowDown && (
            <div className="pointer-events-none absolute end-0 mx-20 flex items-center">
              <Icon name={ICONS.ARROW_DOWN} width=".625rem" />
            </div>
          )}
          {!isDisabled && (
            <ImageValidationStatus
              className={classnames('absolute end-0', {
                'mx-40': hasArrowDown,
                'mx-20': !hasArrowDown,
              })}
              isErrored={isErrored}
              isValidated={isValidated}
            />
          )}
        </div>
        {shouldRenderDatePicker && (
          <div
            className={classnames(
              'animation-duration-500 absolute left-1/2 top-full mt-10 min-w-full -translate-x-1/2 transform rounded-12 border-1 border-black bg-white p-10',
              isActive ? 'animate-fadeIn' : 'animate-fadeOut',
            )}
          >
            <Datepicker
              field={field}
              form={form}
              isDateRange={isDateRange}
              isFormikInput
              locale={language}
              maxDate={maxDate}
              minDate={minDate}
              disabledDays={disabledDays ? [disabledDays] : undefined}
              onSelect={onDaySelect}
              theme="defaultFormikInput"
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};
